import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipDirective } from '../../tooltip/tooltip.directive';
import { SelectOption } from '../../../form-fields/select-field/select-field';
import { ContextMenu } from '../../context-menu/context-menu';
import { ContextMenuTriggerDirective } from '../../context-menu/context-menu-trigger.directive';

/**
 * A pill-shaped split button: a primary toggle area on the left (whose
 * content is projected via `<ng-content>`) and a chevron on the right that
 * opens a popover containing a selectable list of options. Designed for
 * media device pickers (mic, camera, ...) but generic enough for any
 * "toggle + pick from a list" interaction.
 *
 * The tooltip is a host directive on the component element itself — NOT on
 * the inner buttons — so it keeps working while the pill is disabled (native
 * `disabled` controls receive no hover events, and the disabled pill sets
 * `pointer-events: none`, which makes hits fall through to this host). The
 * required `tooltip` input accepts a string or a TemplateRef; the directive's
 * `tooltipTitle` / `tooltipVariant` / `tooltipPosition` / `tooltipDisabled`
 * inputs are available on every usage.
 */
@Component({
  selector: 'andor-split-toggle-button',
  standalone: true,
  imports: [FaDuotoneIconComponent, TranslatePipe, ContextMenu, ContextMenuTriggerDirective],
  hostDirectives: [
    {
      directive: TooltipDirective,
      inputs: [
        'andorTooltip: tooltip',
        'tooltipTitle',
        'tooltipVariant',
        'tooltipPosition',
        'tooltipDisabled',
      ],
    },
  ],
  templateUrl: './split-toggle-button.html',
  styleUrl: './split-toggle-button.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SplitToggleButton {
  active = input<boolean>(false);
  disabled = input<boolean>(false);
  popoverTitle = input<string>('');
  options = input<SelectOption[] | null>([]);
  selectedOptionId = input<string | null>(null);

  toggleClicked = output<void>();
  optionSelected = output<SelectOption>();

  protected readonly faChevronDown = faChevronDown;

  protected onToggle() {
    if (this.disabled()) return;
    this.toggleClicked.emit();
  }

  protected onSelect(option: SelectOption) {
    this.optionSelected.emit(option);
  }
}
